import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ArrowRightIcon,
  BoltIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  CodeBracketSquareIcon,
  DevicePhoneMobileIcon,
  GlobeAltIcon,
  HeartIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

const pillars = [
  {
    title: "Reliable reach",
    description: "We help businesses connect with customers across mobile networks, devices, and regions.",
    icon: GlobeAltIcon,
  },
  {
    title: "Developer-first tools",
    description: "Our APIs and dashboard are built to make messaging, OTP, and customer engagement easier to ship.",
    icon: CodeBracketSquareIcon,
  },
  {
    title: "Practical communication",
    description: "From SMS to USSD and airtime, we focus on channels people already understand and use daily.",
    icon: DevicePhoneMobileIcon,
  },
];

const values = [
  ["Accessibility", "Communication should work for every customer, not only people with data or smartphones."],
  ["Integrity", "We build trust through clear delivery, transparent pricing, and dependable support."],
  ["Innovation", "We keep improving the tools teams need to reach people faster and more intelligently."],
  ["Excellence", "We care about uptime, delivery quality, documentation, and the small details that make teams confident."],
];

const audiences = [
  "Financial services and fintech teams",
  "E-commerce and retail businesses",
  "Schools, NGOs, and public institutions",
  "Startups building customer communication into products",
];

function AboutUs() {
  return (
    <div className="bg-slate-50">
      <Navbar />

      <section className="relative overflow-hidden bg-[#071d33] text-white">
        <div className="absolute inset-x-0 bottom-0 h-24 bg-slate-50" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-[85rem] gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[1fr_0.9fr] lg:px-8 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-300">About Whistle Pulse</p>
            <h1 className="mt-5 text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
              Communication infrastructure for businesses across Africa.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-blue-50">
              Whistle Pulse exists to make mobile communication simple, reliable, and accessible. We help teams send
              messages, verify users, launch USSD services, and keep customers connected in real time.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href="/contact-us"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-950/30 transition hover:bg-blue-400"
              >
                Talk to us
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="/developers"
                className="inline-flex items-center justify-center rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                View developer docs
              </a>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/10 p-4 shadow-2xl shadow-black/30 backdrop-blur">
            <div className="rounded-xl bg-[#041629] p-6">
              <div className="flex items-center justify-between border-b border-white/10 pb-5">
                <div>
                  <p className="text-sm text-blue-200">Platform pulse</p>
                  <p className="mt-1 text-3xl font-bold">Always connected</p>
                </div>
                <span className="rounded-full bg-emerald-400/15 px-3 py-1 text-sm font-semibold text-emerald-200">
                  live
                </span>
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                {[
                  ["Channels", "SMS / USSD"],
                  ["Focus", "Africa"],
                  ["Build", "API-first"],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg bg-white/8 p-4">
                    <p className="text-xs text-blue-200">{label}</p>
                    <p className="mt-2 text-lg font-bold text-white">{value}</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 space-y-3">
                {[
                  "Send high-priority customer messages.",
                  "Verify users with OTP workflows.",
                  "Reach customers without mobile data.",
                  "Support teams with scalable APIs.",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3 rounded-lg border border-white/10 bg-[#071d33] p-4">
                    <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-300" aria-hidden="true" />
                    <p className="text-sm leading-6 text-blue-50">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[85rem] px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-4 md:grid-cols-3">
          {pillars.map((pillar) => (
            <div key={pillar.title} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <pillar.icon className="h-8 w-8 text-blue-600" aria-hidden="true" />
              <h2 className="mt-5 text-xl font-bold text-slate-950">{pillar.title}</h2>
              <p className="mt-3 text-sm leading-6 text-slate-600">{pillar.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto grid max-w-[85rem] gap-10 px-4 pb-16 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:px-8">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-blue-600">Our mission</p>
          <h2 className="mt-4 text-3xl font-bold text-slate-950 sm:text-4xl">
            Build the layer businesses trust for customer communication.
          </h2>
          <p className="mt-5 text-base leading-7 text-slate-600">
            We believe communication should be fast enough for urgent alerts, simple enough for everyday teams, and
            accessible enough to reach customers no matter the device in their hands.
          </p>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200 sm:p-8">
          <div className="grid gap-6 sm:grid-cols-2">
            {[
              [BoltIcon, "Real-time customer engagement"],
              [ShieldCheckIcon, "Secure verification workflows"],
              [UserGroupIcon, "Scalable campaigns and alerts"],
              [BuildingOffice2Icon, "Enterprise-ready support"],
            ].map(([Icon, title]) => (
              <div key={title} className="flex items-start gap-3">
                <Icon className="mt-0.5 h-6 w-6 shrink-0 text-blue-600" aria-hidden="true" />
                <p className="font-semibold leading-6 text-slate-800">{title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto grid max-w-[85rem] gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1fr_1fr] lg:px-8">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-blue-600">Who we serve</p>
            <h2 className="mt-4 text-3xl font-bold text-slate-950 sm:text-4xl">
              Designed for teams that need communication to just work.
            </h2>
            <p className="mt-5 text-base leading-7 text-slate-600">
              From high-growth startups to large organizations, Whistle Pulse supports teams that depend on timely,
              trusted customer communication.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {audiences.map((audience) => (
              <div key={audience} className="rounded-lg border border-slate-200 p-5">
                <CheckCircleIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                <p className="mt-4 text-sm font-semibold leading-6 text-slate-700">{audience}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[85rem] px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <HeartIcon className="mx-auto h-9 w-9 text-blue-600" aria-hidden="true" />
          <p className="mt-5 text-sm font-semibold uppercase tracking-[0.16em] text-blue-600">Our values</p>
          <h2 className="mt-4 text-3xl font-bold text-slate-950 sm:text-4xl">The principles behind the platform.</h2>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {values.map(([title, description]) => (
            <div key={title} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="font-bold text-slate-950">{title}</h3>
              <p className="mt-3 text-sm leading-6 text-slate-600">{description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-[85rem] px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid gap-6 rounded-2xl bg-[#071d33] p-6 text-white sm:p-8 lg:grid-cols-[0.7fr_1.3fr] lg:items-center">
            <div>
              <SparklesIcon className="h-9 w-9 text-blue-300" aria-hidden="true" />
              <h2 className="mt-5 text-2xl font-bold">Ready to connect smarter?</h2>
            </div>
            <div>
              <p className="text-sm leading-7 text-blue-50 sm:text-base">
                Whether you are launching customer alerts, OTPs, USSD services, or messaging campaigns, Whistle Pulse is
                built to help your team move faster with confidence.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <a
                  href="https://dashboard.whistlepulse.com/auth/register"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-full bg-blue-500 px-6 py-3 text-sm font-semibold text-white transition hover:bg-blue-400"
                >
                  Try for free
                </a>
                <a
                  href="/contact-us"
                  className="inline-flex items-center justify-center rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
                >
                  Contact sales
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default AboutUs;
